/* eslint-disable @next/next/no-img-element */
/* eslint-disable @typescript-eslint/no-empty-function */
import { FlipCard } from 'components/elements/FlipCard'
import NewButton from 'components/elements/NewButton'
import Stack from 'components/elements/Stack'
import { Flashcard } from 'interfaces/Flashcard'
import { useEffect, useState } from 'react'
import { Body1, ButtonText, Headline3, Headline4 } from 'stories/elements/Typography/Text'
import theme from 'stories/utils/theme'
import styled, { css } from 'styled-components'
import DownloadButton from '../SEO/DownloadBanner'
import { useToggleState } from 'lib/hooks/useToggleState'
import FeatureDialog from 'stories/elements/Modal/DialogWindow/FeatureDialog'
import _useTranslation from 'lib/hooks/_useTranslation'
import { trackEvent } from 'lib/tracking/analytics-service'
import {
    AnalyticsEventFlashcardComplete,
    AnalyticsEventFlashcardImpression,
    AnalyticsEventFlashcardSetCompleted,
    AnalyticsEventFlashcardsContinueLearningClick,
    AnalyticsEventFlashcardsDialogOpen,
    AnalyticsEventKnowContentView,
} from 'lib/constants/AnalyticsEventType'
import { useRouter } from 'next/router'
import { getReferrerScreen } from 'lib/formatting/url'
import { Know } from 'interfaces/Know'
import { getMobileOS } from 'lib/tracking/attribution/device'
import KnowContentDurationTracking from '../PDFPreview/KnowContentDurationTracking'
import CongratulationDesktopBanner from 'assets/know/congratulation_flashcards_desktop.webp'
import CongratulationMobileBanner from 'assets/know/congratulation_flashcards_mobile.webp'
import useMedia from 'lib/hooks/useMedia'
import { deviceSize } from 'lib/constants/GridBreakpoints'

const Container = styled.div`
    padding: 15px 0;
    @media (max-width: 750px) {
        padding: 15px 0 10px 0;
    }
`

const FlashcardInside = styled.div`
    display: grid;
    gap: 25px;
    padding-top: 15px;
    .stats {
        gap: 15px;
        display: flex;
        justify-content: space-between;
    }
    @media (max-width: 1050px) {
        padding-top: 0;
    }
`

const ButtonsContainer = styled.div`
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
`

const FlashcardBody = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 20px;
    grid-template-columns: 1fr;
    height: 100%;
    width: 100%;
    img {
        object-fit: contain;
    }
`

const KnowArea = styled.div`
    height: auto;
    margin: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    overflow-y: hidden;
    min-width: 400px;

    padding: 10px;
    border-radius: ${theme.borderRadius.normal};
    @media (max-width: 1050px) {
        width: 100%;
        display: block;
    }
`

const ProgressBarWrapper = styled.div<{ value: number; limit: number }>`
    display: grid;
    gap: 10px;
    height: 5px;
    width: 100%;
    border-radius: ${theme.borderRadius.content};
    background-color: ${theme.colors.outlineGray};
    position: relative;
    .bar {
        position: relative;
        border-radius: ${theme.borderRadius.content};
        background-color: ${theme.colors.body1Black};
        width: ${({ value, limit }) => (value / limit) * 100}%;
    }
`

const AnswerCountWrapper = styled.div<{
    backgroundColor: string
    isCorrect?: boolean
    isStatic?: boolean
    isDarkTheme?: boolean
}>`
    width: 50px !important;
    padding: 5px 15px;
    border: 1px solid ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.transparent : theme.colors.violet)};
    display: flex;
    align-items: center;
    gap: 5px;
    justify-content: space-between;
    width: auto;
    position: absolute;
    top: 25px;
    ${({ isCorrect }) =>
        isCorrect
            ? css`
                  right: 0;
                  border-radius: ${theme.borderRadius.large} 0 0 ${theme.borderRadius.large};
              `
            : css`
                  left: 0;
                  border-radius: 0 ${theme.borderRadius.large} ${theme.borderRadius.large} 0;
              `}
    .text {
        display: flex;
        gap: 2.5px;
        align-items: center;
    }
    background-color: ${({ backgroundColor }) => backgroundColor};
    @media (max-width: 750px) {
        width: auto !important;
        top: 15px;
    }
    ${({ isStatic }) =>
        isStatic
            ? css`
                  width: 175px !important;
                  position: static;
                  gap: 20px;
              `
            : ''}
`

const DialogWrapper = styled.div`
    padding: 25px;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    gap: 15px;
    .button {
        width: 100%;
    }
`

const FlashcardTitle = styled(Headline3)<{ isDarkTheme?: boolean }>`
    position: relative;
    top: 0px;
    font-size: 16px;
    text-align: center;
    line-height: 24px;
    color: ${({ isDarkTheme }) => (isDarkTheme ? theme.colors.white : theme.colors.headlineDark)};
`

interface Props {
    flashcards: Flashcard[]
    know: Know
    isDarkTheme?: boolean
}

const KnowFlashcardPreview = ({ flashcards, know, isDarkTheme }: Props) => {
    const [showCongratulationDialog, toggleCongratulationDialog] = useToggleState(false)
    const [selectedFlashcardIndex, setSelectedFlashcardIndex] = useState(0)
    const [correctAnswers, setCorrestAnswers] = useState(0)
    const [wrongAnswers, setWrongAnswers] = useState(0)
    const selectedFlashcard = flashcards[selectedFlashcardIndex]
    const mobileOS = getMobileOS() ?? 'ios'
    const { t } = _useTranslation('knows')
    const { t: tCommon } = _useTranslation('common')
    const router = useRouter()
    const md = useMedia(deviceSize.md)
    const referrerScreen = getReferrerScreen(router)
    const isFinished = selectedFlashcardIndex === flashcards.length

    useEffect(() => {
        trackEvent(AnalyticsEventKnowContentView, {
            referrerScreen,
            know,
        })
    }, [])

    useEffect(() => {
        if (!(selectedFlashcardIndex || selectedFlashcardIndex === 0)) return

        trackEvent(AnalyticsEventFlashcardImpression, {
            referrerScreen,
            know,
            valueId: selectedFlashcardIndex + 1,
            value: flashcards.length.toString(),
        })
    }, [flashcards.length, know, referrerScreen, selectedFlashcardIndex])

    if (!selectedFlashcard) return null

    const showNextFlashcards = (isCorrect?: boolean) => {
        setSelectedFlashcardIndex((index) => (index < flashcards.length - 1 ? ++index : index))
        isCorrect ? setCorrestAnswers((index) => ++index) : setWrongAnswers((index) => ++index)

        trackEvent(AnalyticsEventFlashcardComplete, {
            referrerScreen,
            know,
            value: isCorrect ? 'success' : 'unsure',
            valueId: selectedFlashcardIndex + 1,
        })

        if (selectedFlashcardIndex === flashcards.length - 1) {
            toggleCongratulationDialog()
            const correctAsnwersCount = correctAnswers + (isCorrect ? 1 : 0)
            const wrongAsnwersCount = wrongAnswers + (isCorrect ? 0 : 1)
            // The 0 at the end is for skipped flashcards,
            // since it's not implemented it's static 0
            trackEvent(AnalyticsEventFlashcardSetCompleted, {
                referrerScreen,
                know,
                value: `${correctAsnwersCount}_${wrongAsnwersCount}_0`,
            })
            trackEvent(AnalyticsEventFlashcardsDialogOpen, {
                referrerScreen,
                know,
            })
        }
    }

    const resetProgress = () => {
        setSelectedFlashcardIndex(0)
        setWrongAnswers(0)
        setCorrestAnswers(0)
        trackEvent(AnalyticsEventFlashcardsContinueLearningClick, {
            referrerScreen,
            know,
        })
        showCongratulationDialog && toggleCongratulationDialog()
    }

    const WrongCountBadge = (props: { isStatic?: boolean; isDarkTheme?: boolean }) => (
        <AnswerCountWrapper
            backgroundColor={props.isDarkTheme ? theme.colors.backgroundTransparent : theme.colors.lightRed}
            isStatic={props.isStatic}
            isDarkTheme={props.isDarkTheme}
        >
            <Body1 color={props.isDarkTheme ? theme.colors.errorRed : theme.colors.errorRed} fontFamily="var(--font-inter)">
                {wrongAnswers}
            </Body1>
        </AnswerCountWrapper>
    )

    const CorrectCountBadge = (props: { isStatic?: boolean; isDarkTheme?: boolean }) => (
        <AnswerCountWrapper
            backgroundColor={props.isDarkTheme ? theme.colors.backgroundTransparent : theme.colors.lightGreen}
            isCorrect
            isStatic={props.isStatic}
            isDarkTheme={props.isDarkTheme}
        >
            <Body1
                color={props.isDarkTheme ? theme.colors.subjectGreen : theme.colors.darkGreen}
                fontFamily="var(--font-inter)"
            >
                {correctAnswers}
            </Body1>
        </AnswerCountWrapper>
    )

    const ContinueSection = () => (
        <>
            <span />
            <DownloadButton
                type={mobileOS}
                referrerScreen={'flashcard-preview'}
                isDark
                backgroundColor={theme.colors.deepSeaBlue}
            />
            <NewButton
                borderRadius={theme.borderRadius.normal}
                color={theme.colors.deepSeaBlue}
                fullWidth
                type="secondary"
                onClick={resetProgress}
                height={50}
            >
                <ButtonText color={theme.colors.deepSeaBlue}>
                    {t('knows/flashcardsCongratulationContinueLearningButton')}
                </ButtonText>
            </NewButton>
        </>
    )

    const Headline = md ? Headline3 : Headline4
    const CongratulationImage = !md ? CongratulationDesktopBanner : CongratulationMobileBanner

    return (
        <Container>
            <KnowContentDurationTracking know={know} />
            <FlashcardTitle isDarkTheme={isDarkTheme}>{tCommon('common/navBarFlashcardMenuItem')}</FlashcardTitle>
            <KnowArea>
                <FlashcardInside>
                    <div>
                        <Stack gutter={10}>
                            <div className="stats">
                                <WrongCountBadge isDarkTheme={isDarkTheme} />
                                <CorrectCountBadge isDarkTheme={isDarkTheme} />
                            </div>
                            <ButtonText
                                color={isDarkTheme ? theme.colors.white : theme.colors.body1Black}
                                textAlign="center"
                            >{`${selectedFlashcardIndex + 1} / ${flashcards.length}`}</ButtonText>
                            <ProgressBarWrapper limit={flashcards.length} value={selectedFlashcardIndex + 1}>
                                <div className="bar" />
                            </ProgressBarWrapper>
                        </Stack>
                    </div>
                    <FlipCard align={'left'} resetCondition={selectedFlashcardIndex} isDarkTheme={isDarkTheme}>
                        <div className="flip-card-inner">
                            {md ? (
                                <>
                                    <div className="flip-card-back">
                                        <FlashcardBody className="content question">
                                            {selectedFlashcard.questionImageFilePath ? (
                                                <img
                                                    src={selectedFlashcard.questionImageFilePath}
                                                    width={200}
                                                    height={200}
                                                    alt="question study help"
                                                />
                                            ) : null}
                                            <Body1
                                                textAlign="center"
                                                fontFamily="var(--font-inter)"
                                                fontWeight={400}
                                                color={isDarkTheme ? theme.colors.white : theme.colors.headlineDark}
                                            >
                                                {selectedFlashcard.questionText}
                                            </Body1>
                                        </FlashcardBody>
                                        <FlashcardBody className="content answer">
                                            {selectedFlashcard.answerImageFilePath ? (
                                                <img
                                                    src={selectedFlashcard.answerImageFilePath}
                                                    width={200}
                                                    height={200}
                                                    alt="answer study help"
                                                />
                                            ) : null}
                                            <Body1
                                                textAlign="center"
                                                fontFamily="var(--font-inter)"
                                                fontWeight={400}
                                                color={isDarkTheme ? theme.colors.white : theme.colors.headlineDark}
                                            >
                                                {selectedFlashcard.answerText}
                                            </Body1>
                                        </FlashcardBody>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <div className="flip-card-front">
                                        <FlashcardBody className="content">
                                            {selectedFlashcard.answerImageFilePath ? (
                                                <img
                                                    src={selectedFlashcard.answerImageFilePath}
                                                    width={200}
                                                    height={200}
                                                    alt="answer study help"
                                                />
                                            ) : null}
                                            <Body1
                                                textAlign="center"
                                                fontFamily="var(--font-inter)"
                                                fontWeight={400}
                                                color={isDarkTheme ? theme.colors.white : theme.colors.headlineDark}
                                            >
                                                {selectedFlashcard.answerText}
                                            </Body1>
                                        </FlashcardBody>
                                    </div>

                                    <div className="flip-card-back">
                                        <FlashcardBody className="content">
                                            {selectedFlashcard.questionImageFilePath ? (
                                                <img
                                                    src={selectedFlashcard.questionImageFilePath}
                                                    width={200}
                                                    height={200}
                                                    alt="question study help"
                                                />
                                            ) : null}
                                            <Body1
                                                textAlign="center"
                                                fontFamily="var(--font-inter)"
                                                fontWeight={400}
                                                color={isDarkTheme ? theme.colors.white : theme.colors.headlineDark}
                                            >
                                                {selectedFlashcard.questionText}
                                            </Body1>
                                        </FlashcardBody>
                                    </div>
                                </>
                            )}
                        </div>
                    </FlipCard>
                    <ButtonsContainer>
                        {isFinished ? (
                            <ContinueSection />
                        ) : (
                            <>
                                <NewButton
                                    borderRadius={theme.borderRadius.normal}
                                    color={`${isDarkTheme ? theme.colors.headlineDark : theme.colors.body2Black}44`}
                                    fullWidth
                                    type={isDarkTheme ? 'primary' : 'secondary'}
                                    onClick={() => showNextFlashcards(false)}
                                    height={50}
                                >
                                    <ButtonText
                                        color={isDarkTheme ? theme.colors.white : theme.colors.body1Black}
                                        fontFamily="var(--font-inter)"
                                        fontWeight={500}
                                    >
                                        {t('knows/flashcardsNotSureButton')}
                                    </ButtonText>
                                </NewButton>
                                <NewButton
                                    borderRadius={theme.borderRadius.normal}
                                    color={`${isDarkTheme ? theme.colors.headlineDark : theme.colors.body2Black}44`}
                                    type={isDarkTheme ? 'primary' : 'secondary'}
                                    fullWidth
                                    onClick={() => showNextFlashcards(true)}
                                    height={50}
                                >
                                    <ButtonText
                                        color={isDarkTheme ? theme.colors.white : theme.colors.body1Black}
                                        fontFamily="var(--font-inter)"
                                        fontWeight={500}
                                    >
                                        {t('knows/flashcardsCorrectButton')}
                                    </ButtonText>
                                </NewButton>
                            </>
                        )}
                    </ButtonsContainer>
                </FlashcardInside>
            </KnowArea>
            <FeatureDialog
                open={showCongratulationDialog}
                onClose={toggleCongratulationDialog}
                ariaPrefix="congratulation-flashcard"
                title=""
                maxWidth={500}
            >
                <DialogWrapper>
                    <Headline textAlign="center" fontFamily="var(--font-inter)" color={theme.colors.headlineDark}>
                        {t('knows/flashcardsCongratulationTitle')}
                    </Headline>
                    <Headline fontFamily="var(--font-inter)" textAlign="center" color={theme.colors.deepSeaBlue}>
                        {t('knows/flashcardsCongratulationSaveProgress')}
                    </Headline>
                    <img
                        src={CongratulationImage.src}
                        width={CongratulationImage.width}
                        height={CongratulationImage.height}
                        alt=""
                    />
                    {/* <Body1 textAlign="center" fontFamily="var(--font-inter)" fontWeigth={400}>
                        {t(
                            correctAnswers === flashcards.length
                                ? 'knows/flashcardsCongratulationCorrectText'
                                : 'knows/flashcardsCongratulationAlmostCorrectText'
                        )}
                    </Body1> */}

                    <ContinueSection />
                </DialogWrapper>
            </FeatureDialog>
        </Container>
    )
}

export default KnowFlashcardPreview
